import { dinocFilms } from 'enums/dinocFilms';

/** First non-empty external image URL (API often sends `""` instead of omitting the field). */
function externalImageFromFilmData(fd) {
  if (!fd) {
    return undefined;
  }
  const candidates = [
    fd.externalThumbnailUrl,
    fd.external_thumbnail_url,
    fd.externalImageUrl,
    fd.external_image_url,
    fd.externalImageURL,
  ];
  for (const u of candidates) {
    if (u != null && String(u).trim() !== '') {
      return u;
    }
  }
  return undefined;
}

/**
 * Wrapping flow: `/media/wrapping/{slug}.png`, or Fasara `Choose.jpg` when nothing selected.
 *
 * @param {string|number|null|undefined} wrappingFilmSlug — form field `sheets[].sheet`
 */
export function getWrappingSheetPreviewSrc(wrappingFilmSlug) {
  const publicBase = process.env.PUBLIC_URL || '';
  if (wrappingFilmSlug != null && String(wrappingFilmSlug).trim() !== '') {
    return `${publicBase}/media/wrapping/${String(
      wrappingFilmSlug,
    ).trim()}.png`;
  }
  return `${publicBase}/media/fasara/Choose.jpg`;
}

export function getFasaraViewSheetImgSrc(sheet) {
  const publicBase = process.env.PUBLIC_URL || '';
  const fd = sheet?.filmData;
  const fromFilmData = externalImageFromFilmData(fd);
  if (fromFilmData) {
    return fromFilmData;
  }
  const slug =
    (fd &&
      (fd.code ??
        fd.filmCode ??
        fd.sku ??
        (typeof fd.value === 'string' ? fd.value : null) ??
        (typeof fd.name === 'string' ? fd.name.trim() || null : null))) ??
    sheet?.film;
  if (slug == null || slug === '') {
    return `${publicBase}/media/fasara/Choose.jpg`;
  }
  if (String(slug) === 'Custom') {
    return `${publicBase}/media/fasara/Custom.jpg`;
  }
  return `${publicBase}/media/fasara/${String(slug).trim()}.jpg`;
}

function buildFilePath(family, film) {
  const films = dinocFilms.filter((e) => e.family == family);
  const matchedFilms = films.filter((e) => e.value == film);
  if (matchedFilms.length === 1) {
    return `${film}.jpg`;
  }
  if (film && film !== 'Custom' && typeof film === 'string') {
    const slug = String(film).trim();
    if (slug !== '') {
      return `${slug}.jpg`;
    }
  }
  return 'Custom.jpg';
}

/** API catalog row for the selected film. */
function findDinocProductRow(familyProducts, film) {
  if (!Array.isArray(familyProducts) || film == null || film === '') {
    return null;
  }
  const needle = String(film).trim();
  return familyProducts.find((p) => {
    const keys = [
      p.id,
      p.value,
      p.code,
      p.filmId,
      p.sku,
      p.name,
      p.label,
    ].filter((k) => k != null && k !== '');
    return keys.some((k) => String(k).trim() === needle);
  });
}

/** `/media/dinoc/*.jpg` file name: prefer API code/sku, else legacy enum mapping. */
function getDinocStaticPreviewFile({ family, film, familyProducts, filmData }) {
  if (!film || film === 'Custom') {
    return 'Custom.jpg';
  }
  const row =
    findDinocProductRow(familyProducts, film) ??
    findDinocProductRow(filmData ? [filmData] : [], film);
  const fromApi =
    row &&
    (row.code ??
      row.filmCode ??
      row.sku ??
      (typeof row.value === 'string' ? row.value : null) ??
      (typeof row.name === 'string' ? row.name.trim() || null : null));
  if (fromApi != null && String(fromApi).trim() !== '') {
    return `${String(fromApi).trim()}.jpg`;
  }
  return buildFilePath(family, film);
}

function getDinocLocalFilmPreviewSrc({
  family,
  film,
  familyProducts,
  filmData,
}) {
  const publicBase = process.env.PUBLIC_URL || '';
  const file = getDinocStaticPreviewFile({
    family,
    film,
    familyProducts,
    filmData,
  });
  return `${publicBase}/media/dinoc/${file}`;
}

/**
 * Preview image for a DI-NOC sheet: API / catalog URLs when present, else static `/media/dinoc/`.
 *
 * @param {{ family?: string, film?: string, familyProducts?: Array, filmData?: Record<string, unknown> }} [args]
 */
export function getDinocSheetPreviewSrc({
  family,
  film,
  familyProducts,
  filmData,
} = {}) {
  const local = getDinocLocalFilmPreviewSrc({
    family,
    film,
    familyProducts,
    filmData,
  });

  if (!film || film === 'Custom') {
    return local;
  }

  const fd = filmData;
  const fromSaved = externalImageFromFilmData(fd);
  if (fromSaved) {
    return fromSaved;
  }

  const match =
    findDinocProductRow(familyProducts, film) ??
    findDinocProductRow(fd ? [fd] : [], film);
  const fromCatalog = externalImageFromFilmData(match);
  if (fromCatalog) {
    return fromCatalog;
  }

  return local;
}
